import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import GlossaryEditor from './Pipeline/GlossaryEditor';
import ReplacementsEditor from './Pipeline/ReplacementsEditor';

// The review queue of a quest: every place where the stress model heard a
// different vowel than the text expects, across the active takes plus the
// versions somebody pinned. A row is judged by ear with three outcomes:
//   speaker right  -> verdict «ok» on the take; the text's expectation was the
//                     wrong one, and the heard stress can go into the glossary
//   speaker wrong  -> verdict «wrong» (the take joins the re-voice queue)
//   skip           -> nothing stored, next row
// Playing happens in the studio's own player (onOpen switches it to the take
// and plays the word with a little context), so there is one player, one
// text pane and one highlight for everything.

const VOWELS = 'аеёиоуыэюяАЕЁИОУЫЭЮЯ';
const FIELD_SHORT = { description: 'descr', after_message: 'after' };

// The form with a stress mark on vowel number `vi` (0-based); no mark when null.
export function withStress(word, vi) {
  const plain = String(word || '').replace(/́/g, '');
  if (vi == null) return plain;
  let n = -1;
  let out = '';
  for (const ch of plain) {
    out += ch;
    if (VOWELS.includes(ch)) { n += 1; if (n === vi) out += '́'; }
  }
  return out;
}

const keyOf = (r) => `${r.versionId}/${r.wordIndex}`;

export default function StressReview({ questId, locations, playingKey, onOpen, onChanged }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [sort, setSort] = useState('order');          // order | conf
  const [unheardOnly, setUnheardOnly] = useState(false);
  const [cursor, setCursor] = useState(null);         // key of the row the keys act on
  const [busy, setBusy] = useState(null);             // key being written
  const [note, setNote] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pick, setPick] = useState('');
  const [showReplacements, setShowReplacements] = useState(false);
  const [glossaryFor, setGlossaryFor] = useState(null);   // the row whose form the glossary editor opens on
  const listRef = useRef(null);

  const load = useCallback(() => {
    if (!questId) return;
    fetch(`/api/pipeline/quest/${encodeURIComponent(questId)}/stress-review`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : r.json().then((d) => Promise.reject(new Error(d.error || `HTTP ${r.status}`)))))
      .then((d) => { setData(d); setError(null); })
      .catch((e) => setError(e.message));
  }, [questId]);
  useEffect(() => { load(); }, [load]);

  // A pinned take whose check is still on its way: ask again until it lands.
  useEffect(() => {
    if (!data || !data.pending.length) return undefined;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [data, load]);

  // Progress of a batch of checks: how many were waiting when it started, when
  // each one landed (for the pace), and a clock for the running one.
  const progRef = useRef({ initial: 0, landed: [] });
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!data) return;
    const pend = data.pending.filter((p) => !(p.task && p.task.status === 'failed')).length;
    const pr = progRef.current;
    if (pend > pr.initial - pr.landed.length) { pr.initial = pend + pr.landed.length; }
    if (pend < pr.initial - pr.landed.length) {
      const gone = (pr.initial - pr.landed.length) - pend;
      for (let i = 0; i < gone; i += 1) pr.landed.push(Date.now());
    }
    if (pend === 0) progRef.current = { initial: 0, landed: [] };
  }, [data]);
  useEffect(() => {
    if (!data || !data.pending.length) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [data]);

  const rows = useMemo(() => {
    if (!data) return [];
    let list = data.items.filter((r) => !unheardOnly || !r.verdict || r.verdictStale);
    if (sort === 'conf') list = [...list].sort((a, b) => (b.conf ?? 0) - (a.conf ?? 0));
    return list;
  }, [data, sort, unheardOnly]);

  // The cursor follows what is being played, and never points past the list.
  useEffect(() => {
    if (playingKey && rows.some((r) => keyOf(r) === playingKey)) setCursor(playingKey);
  }, [playingKey, rows]);
  useEffect(() => {
    if (rows.length && (!cursor || !rows.some((r) => keyOf(r) === cursor))) setCursor(keyOf(rows[0]));
  }, [rows, cursor]);

  const idx = rows.findIndex((r) => keyOf(r) === cursor);
  const current = idx >= 0 ? rows[idx] : null;

  const step = useCallback((d) => {
    if (!rows.length) return;
    const i = Math.min(rows.length - 1, Math.max(0, (idx < 0 ? 0 : idx) + d));
    const r = rows[i];
    setCursor(keyOf(r));
    onOpen(r);
    const el = listRef.current && listRef.current.querySelector(`[data-key="${keyOf(r)}"]`);
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
  }, [rows, idx, onOpen]);

  const judge = useCallback(async (r, ok) => {
    const k = keyOf(r);
    setBusy(k);
    setNote(null);
    try {
      const res = await fetch('/api/word-verdict', {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ versionId: r.versionId, wordIndex: r.wordIndex, form: r.form, spelling: r.spelling, ok }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || `HTTP ${res.status}`);
      setData((d) => d && ({ ...d, items: d.items.map((x) => (keyOf(x) === k
        ? { ...x, verdict: ok === null ? null : (ok ? 'ok' : 'wrong'), verdictStale: false, judgedBy: ok === null ? null : 'you' } : x)) }));
      if (onChanged) onChanged();
      // The row stays, marked, with its undo; the cursor moves on to the next
      // row nobody has heard yet and plays it.
      if (ok !== null) {
        const next = rows.slice(idx + 1).find((x) => keyOf(x) !== k && (!x.verdict || x.verdictStale));
        if (next) { setCursor(keyOf(next)); onOpen(next); }
      }
    } catch (e) {
      setNote(`Could not save the verdict: ${e.message}`);
    } finally {
      setBusy(null);
    }
  }, [rows, idx, onOpen, onChanged]);

  const pin = useCallback(async (versionId, on) => {
    setNote(null);
    try {
      const res = await fetch(`/api/audio-versions/${versionId}/review-pin`, { method: on ? 'POST' : 'DELETE', credentials: 'include' });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || `HTTP ${res.status}`);
      if (on) setNote(d.taskId ? `Version ${versionId} pinned — its timings and stress check are being measured` : `Version ${versionId} pinned`);
      setPick('');
      load();
    } catch (e) {
      setNote(`Could not change the pin: ${e.message}`);
    }
  }, [load]);

  // Keys, while the review tab is up and no field has the focus:
  // ↓/↑ or j/k next/previous (and play), space replay, 1 speaker right,
  // 2 speaker wrong, 3 skip, g into the glossary.
  useEffect(() => {
    const onKey = (e) => {
      const t = e.target;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === 'ArrowDown' || e.key === 'j') { e.preventDefault(); step(1); }
      else if (e.key === 'ArrowUp' || e.key === 'k') { e.preventDefault(); step(-1); }
      else if (e.key === ' ' && current) { e.preventDefault(); onOpen(current); }
      else if (e.key === '1' && current && (!current.verdict || current.verdictStale)) { e.preventDefault(); judge(current, true); }
      else if (e.key === '2' && current && (!current.verdict || current.verdictStale)) { e.preventDefault(); judge(current, false); }
      else if (e.key === 'u' && current && current.verdict) { e.preventDefault(); judge(current, null); }
      else if (e.key === '3') { e.preventDefault(); step(1); }
      else if (e.key === 'g' && current) { e.preventDefault(); setGlossaryFor(current); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [step, judge, onOpen, current]);

  if (error) return <div className="as-warn">Stress review: {error}</div>;
  if (!data) return <div className="as-note">Loading the review queue…</div>;

  const unchecked = data.versions.filter((v) => v.active && !v.hasStress && !data.pending.some((p) => p.versionId === v.versionId && p.task && p.task.status !== 'failed'));
  const checkAll = async (list) => {
    setNote(`Queuing ${list.length} take${list.length === 1 ? '' : 's'}…`);
    for (const v of list) {
      try { await fetch(`/api/audio-versions/${v.versionId}/review-pin`, { method: 'POST', credentials: 'include' }); } catch (e) { /* the next one still goes */ }
    }
    setNote(null);
    load();
  };
  const total = data.items.length;
  const open = data.items.filter((r) => !r.verdict || r.verdictStale).length;
  const cover = data.versions.filter((v) => (v.active || v.pinned) && v.summary).reduce((c, v) => ({
    takes: c.takes + 1, checked: c.checked + v.summary.checked, ok: c.ok + v.summary.ok, mismatch: c.mismatch + v.summary.mismatch,
    noExpect: c.noExpect + v.summary.noExpect, pair: c.pair + v.summary.pair, acute: c.acute + v.summary.acute,
  }), { takes: 0, checked: 0, ok: 0, mismatch: 0, noExpect: 0, pair: 0, acute: 0 });
  const candidates = data.versions.filter((v) => !v.active && !v.pinned);

  return (
    <div className="sr">
      <div className="sr-head">
        <span className="sr-count">
          <strong>{open}</strong> to hear · {total - open} judged
          {data.pending.length ? ` · ${data.pending.length} waiting for a check` : ''}
        </span>
        <span className="sr-cover" title="every multi-syllabic word of the takes in this queue went through the stress model; «by the two stress systems» = RUAccent and silero-stress agreed on the expected stress, «by the glossary» = a mark in the text">
          checked {cover.checked} words in {cover.takes} take{cover.takes === 1 ? '' : 's'}: {cover.pair} by the two stress systems, {cover.acute} by the glossary
          {cover.noExpect ? `, ${cover.noExpect} with nothing to compare` : ''} · {cover.ok} agree, {cover.mismatch} differ
        </span>
        <label className="sr-ctl">sort
          <select value={sort} onChange={(e) => setSort(e.target.value)}>
            <option value="order">by point</option>
            <option value="conf">by confidence</option>
          </select>
        </label>
        <label className="sr-ctl"><input type="checkbox" checked={unheardOnly} onChange={(e) => setUnheardOnly(e.target.checked)} /> unheard only</label>
        <button className="as-ghost" onClick={() => setPickerOpen((v) => !v)}>{pickerOpen ? 'close' : '+ pin a version'}</button>
        <button className="as-ghost" onClick={() => setShowReplacements(true)} title="regex replacements run on every text on its way to TTS, before the glossary">replacements…</button>
        <span className="sr-keys" title="keys: ↓/↑ next/previous and play · space replay · 1 speaker right · 2 speaker wrong · 3 skip · u undo · g open the glossary on this word">keys: ↓ ↑ ␣ 1 2 3 u g</span>
      </div>
      {pickerOpen ? (
        <div className="sr-picker">
          <select value={pick} onChange={(e) => setPick(e.target.value)}>
            <option value="">pick a version to put on review…</option>
            {candidates.map((v) => (
              <option key={v.versionId} value={v.versionId}>
                {v.pointNo}. {v.locationName} · {FIELD_SHORT[v.fieldType] || v.fieldType} · #{v.versionId}
                {v.label ? ` · ${v.label}` : ''}{v.hasStress ? '' : v.hasAlignment ? ' · no stress check yet' : ' · no timings yet'}
              </option>
            ))}
          </select>
          <button className="as-ghost" disabled={!pick} onClick={() => pin(Number(pick), true)}>pin</button>
          {data.versions.filter((v) => v.pinned).map((v) => (
            <span key={v.versionId} className="sr-pin">
              #{v.versionId} · {v.pointNo}. {FIELD_SHORT[v.fieldType] || v.fieldType}{v.pinnedBy ? ` · ${v.pinnedBy}` : ''}
              <button className="sr-x" title="unpin" onClick={() => pin(v.versionId, false)}>×</button>
            </span>
          ))}
        </div>
      ) : null}
      {data.pending.length ? (() => {
        const live = data.pending.filter((p) => !(p.task && p.task.status === 'failed'));
        const running = live.filter((p) => p.task && p.task.status === 'running');
        const pr = progRef.current;
        const done = pr.landed.length;
        // Pace: the mean gap between landings once two have landed; 40 s before that
        // (a warm Colab session takes about that per take; a cold one adds minutes).
        const gaps = pr.landed.slice(1).map((t, i) => t - pr.landed[i]);
        const pace = gaps.length ? gaps.reduce((a, b) => a + b, 0) / gaps.length : 40000;
        const eta = Math.round(live.length * pace / 60000);
        const elapsed = running.length && running[0].task.runStartedAt ? Math.round((now - running[0].task.runStartedAt) / 1000) : null;
        return (
          <div className="sr-progress">
            <strong>stress check:</strong> {done} done · {live.length - running.length} queued · {running.length} running{elapsed != null ? ` (${elapsed} s)` : ''}
            {live.length ? ` · ~${eta < 1 ? '<1' : eta} min left` : ''}
            {done + live.length ? <span className="sr-bar"><span style={{ width: `${Math.round(100 * done / (done + live.length))}%` }} /></span> : null}
          </div>
        );
      })() : null}
      {data.pending.map((p) => (
        <div key={p.versionId} className="as-note as-note-flat">
          #{p.versionId} · {p.pointNo}. {p.locationName} · {FIELD_SHORT[p.fieldType] || p.fieldType}:
          {p.task ? ` ${p.task.status === 'failed' ? `check failed — ${p.task.error || 'see the run output in the Editor tab'}` : `measuring… (${p.task.status})`}`
            : p.hasAlignment ? ' timings exist, no stress check — re-align to get one' : ' waiting for timings'}
        </div>
      ))}
      {note ? <div className="as-note as-note-flat">{note}</div> : null}

      {unchecked.length ? (
        <div className="as-note as-note-flat">
          {unchecked.length} active take{unchecked.length === 1 ? ' has' : 's have'} no stress check yet
          {' '}<button className="as-ghost" onClick={() => checkAll(unchecked)} title="measure timings and run the stress check for every active take that has none; rows appear here as each one lands">check them all</button>
        </div>
      ) : null}

      {rows.length === 0 ? (
        <div className="as-note">{total ? 'Everything here has been heard.' : 'No stress mismatches in the active takes.'}</div>
      ) : (
        <div className="sr-list" ref={listRef}>
          <div className="sr-row sr-row-head">
            <span>point</span>
            <span>word: text expects → model heard</span>
            <span>glossary now</span>
            <span>verdict</span>
          </div>
          {rows.map((r) => {
            const k = keyOf(r);
            const isCur = k === cursor;
            const isPlaying = k === playingKey;
            const gl = r.glossary ? (r.glossary.stressed || '∅ no mark') : null;
            return (
              <div key={k} data-key={k}
                   className={`sr-row${isCur ? ' sr-row-cur' : ''}${isPlaying ? ' sr-row-live' : ''}${r.verdict && !r.verdictStale ? ` sr-row-${r.verdict}` : ''}`}
                   onClick={() => { setCursor(k); onOpen(r); }}>
                <span className="sr-where" title={r.locationName}>
                  {r.pointNo}. {FIELD_SHORT[r.fieldType] || r.fieldType}
                  {!r.active ? <span className="sr-tag" title="not the active version">#{r.versionId}</span> : null}
                </span>
                <span className="sr-word" title={`${r.expSrc === 'acute' ? 'expected from a mark in the text (glossary)' : 'expected from RUAccent + silero-stress'} · confidence ${r.conf != null ? r.conf.toFixed(2) : '?'}`}>
                  <b className="sr-exp">{withStress(r.spelling, r.exp)}</b>
                  <span className="sr-arrow"> → </span>
                  <b className="sr-heard">{withStress(r.spelling, r.heard)}</b>
                </span>
                <span className="sr-gl" onClick={(e) => e.stopPropagation()}>
                  {gl ? <span title={`glossary entry «${r.form}»: ${gl}${r.glossary.status ? ` (${r.glossary.status})` : ''}`}>{gl}</span> : <span className="sr-dim">not in glossary</span>}
                  <button className="as-ghost" onClick={() => setGlossaryFor(r)} title="open the quest glossary on this word (g) — add or change the entry there, nothing is written from here">glossary…</button>
                </span>
                <span className="sr-actions" onClick={(e) => e.stopPropagation()}>
                  <button className="as-ghost" disabled={busy === k} onClick={() => onOpen(r)} title="play (space)">▶</button>
                  {r.verdict && !r.verdictStale ? (
                    <span className={`sr-judged sr-judged-${r.verdict}`}>
                      {r.verdict === 'ok' ? '✓ speaker right' : '✗ speaker wrong'}{r.judgedBy ? ` · ${r.judgedBy}` : ''}
                      <button className="as-ghost" disabled={busy === k} onClick={() => judge(r, null)} title="take the verdict back (u)">undo</button>
                    </span>
                  ) : (
                    <>
                      <button className="as-ghost" disabled={busy === k} onClick={() => judge(r, true)} title="the speaker is right, the text expected the wrong stress (1)">speaker right</button>
                      <button className="as-ghost" disabled={busy === k} onClick={() => judge(r, false)} title="the speaker misplaced the stress — the take goes to the re-voice queue (2)">speaker wrong</button>
                    </>
                  )}
                  {r.verdict && r.verdictStale ? <span className="sr-stale" title="judged against another spelling — hear again">stale</span> : null}
                </span>
              </div>
            );
          })}
        </div>
      )}
      {showReplacements ? (
        <ReplacementsEditor
          questId={questId}
          onClose={() => setShowReplacements(false)}
          onSaved={() => { load(); if (onChanged) onChanged(); }}
        />
      ) : null}
      {glossaryFor ? (
        <GlossaryEditor
          questId={questId}
          locations={locations}
          initialFilter={glossaryFor.form}
          onClose={() => setGlossaryFor(null)}
          onSaved={() => { load(); if (onChanged) onChanged(); }}
        />
      ) : null}
    </div>
  );
}